import axios from "axios";
import {
  getHttpProtocol as scheme,
  getSubscriptionServiceUrl,
} from "../utils/requests";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";

// custom axios
const subscriptionAxios = axios.create({
  baseURL: `${scheme}//${getSubscriptionServiceUrl}`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

subscriptionAxios.interceptors.request.use(requestAccessTokenInterceptor);

subscriptionAxios.interceptors.request.use(refreshAccessTokenInterceptor);

subscriptionAxios.interceptors.response.use(responseStatusInterceptor);

// endpoints

export type GetPlansResult = {
  data: {
    uuid: string;
    name: string;
    active: boolean;
    deleted: boolean;
    allowCheckins: boolean;
    allowOpenGymCheckins: boolean;
    hasCalendarItemTypeRestriction: boolean;
    isCheckinWindowTiedToBillingWindow: boolean;
    planTypeId: number;
    clientUuid: string;
    planCategoryId: number;
    type: string;
    expiration: number;
    expirationType: string;
    limitCheckins: number;
    limitCheckinsType: string;
    isMembership: boolean;
    isPublic: boolean;
    isTrial: boolean;
    interval: number;
    intervalType: string;
    setupFee: string;
    amount: string;
    description: string;
    additionalInfo: string;
    taxRateId: number;
    landingPageRedirectTo: string;
    redirectUrl: string;
    occurrences: number;
    quantityAvailable: number;
    limitQuantity: boolean;
    confirmationMessage: string;
    createTimestamp: number;
    showInKioskMode: boolean;
    hasEntitlements: boolean;
    listOnLandingPages: boolean;
    category: {
      uuid: string;
      name: string;
      active: boolean;
    };
    recurring: string;
    numberSessions: string;
    sessionsPeriod: string;
    sessionsPerPeriod: string;
    activeSubscriptionsCount: number;
    perPersonLimit?: boolean;
    perPersonLimitNum?: number;
    hasFixedBillingDayOfMonth: boolean;
    fixedBillingDayOfMonth?: number;
    allowCustomizableStartDate: boolean;
  }[];
};

export async function getPlans(
  clientUuid: string,
  include?: "activeSubscriptionsCount"[],
) {
  const query = ["limit=1000"];
  if (include) {
    query.push(`include=${include.join(",")}`);
  }
  const url = `/${clientUuid}/plan?${query.join("&")}`;
  const response = await subscriptionAxios.get<GetPlansResult>(url);
  return response.data;
}

export type PlanType = "recurring" | "non-recurring" | "session-pack";

export type ClientStatusType = "member" | "non-member" | "lead";

export type IntervalType = "day" | "week" | "month" | "year";

export type ExpirationType = "day" | "week" | "month" | "year";

export type LimitCheckinsType = "billing-period" | "week" | "month";

interface BasePlan {
  // type
  type: PlanType;

  // basic
  name: string;
  clientStatus: ClientStatusType;
  description?: string;
  additionalInfo?: string;

  // availability
  publishToPublic: boolean;
  showOnMemberPortal: boolean;
  showKioskMode: boolean;
  showOnLandingPages: boolean;
  confirmationMessage?: string;
  allowClassBooking: boolean;

  // billing
  amount: number;
  interval: number;
  intervalType: IntervalType;
  recursForever: boolean;
  occurrences?: number;
  setupFee: number;
  expiration?: number;
  expirationType?: ExpirationType;

  // class and open gym
  allowCheckins: boolean;
  allowOpenGymCheckins: boolean;
  limitCheckins: boolean;
  limitCheckinsQuantity?: number;
  limitCheckinsType?: LimitCheckinsType;

  // other
  planIs24HourAccess: boolean;
  limitQuantity: boolean;
  quantityAvailable?: number;
  priorityBooking: boolean;
  priorityBookingMinutes?: number;
  perPersonLimit?: boolean;
  perPersonLimitNum?: number;
  hasFixedBillingDayOfMonth: boolean;
  fixedBillingDayOfMonth?: number;
  allowCustomizableStartDate: boolean;
}

export interface CreatePlanBody extends BasePlan {
  planCategoryUuid: string;
  taxRateUuid?: string;
  checkinRestrictedCalendarTypeUuids?: string[];
  appointmentCredits?: {
    appointmentTypeUuid: string;
    quantity: number;
    expirationDays: number;
    value: number;
  }[];
}

export interface UpdatePlanBody extends CreatePlanBody {
  planUuid: string;
  updateSubscriptions?: {
    amount?: boolean;
    checkins?: boolean;
  };
}

export interface Plan extends BasePlan {
  planUuid: string;
  planCategory?: {
    uuid: string;
    name: string;
  };
  taxRate?: {
    uuid: string;
    name: string;
    rate: number;
  };
  appointmentCredits: {
    appointmentType: {
      uuid: string;
      name: string;
      color: string;
    };
    quantity: number;
    expirationDays: number;
    value?: number;
  }[];
  checkinRestrictedCalendarTypes: {
    uuid: string;
    name: string;
    description?: string;
    color: string;
  }[];
  activeSubscriptionsCount?: number;
}

export interface CreatePlanPayload {
  body: CreatePlanBody;
}

export interface CreatePlanResult {
  data: CreatePlanBody & {
    planUuid: string;
  };
}

export async function postPlan(payload: CreatePlanPayload) {
  const url = `/plan`;
  const response = await subscriptionAxios.post<CreatePlanResult>(
    url,
    payload.body,
  );
  return response.data;
}

export interface PlanCategory {
  uuid: string;
  name: string;
  slug: string;
  active?: boolean;
}

export interface GetPlanCategoriesResult {
  data: PlanCategory[];
}

export async function getPlanCategories(clientId: number) {
  const url = `/plan-categories?client_id=${clientId}&active=1`;
  const response = await subscriptionAxios.get<GetPlanCategoriesResult>(url);
  return response.data;
}

export async function getPlanCategory(planCategoryUuid: string) {
  const url = `/plan-category/${planCategoryUuid}`;
  const response = await subscriptionAxios.get<PlanCategory>(url);
  return response.data;
}

export type GetPlanResult = Plan;

export async function getPlan(
  planUuid: string,
  options: { include?: "activeSubscriptionsCount"[] } = {},
) {
  const url = `/plan/${planUuid}`;
  const response = await subscriptionAxios.get<GetPlanResult>(url, {
    params: { include: options.include },
  });
  return response.data;
}

export interface UpdatePlanPayload {
  body: UpdatePlanBody;
}

export interface UpdatePlanResult {
  data: UpdatePlanBody;
}

export async function putPlan(payload: UpdatePlanPayload) {
  const url = `/plan/${payload.body.planUuid}`;
  const response = await subscriptionAxios.put<UpdatePlanResult>(
    url,
    payload.body,
  );
  return response.data;
}

export interface GetSubscriptionMigratePreviewParams {
  subscriptionUuid: string;
  newPlanUuid: string;
  discountUuid: string;
  billingUuid: string;
  when: "now" | "nextBillDate";
  customSetupFee?: number;
}

export type SubscriptionMigratePreview = {
  total: number;
  subtotal: number;
  fees: {
    description: string;
    code: string;
    fee: number;
    rate: number;
  }[];
  discount: {
    rate: number;
    type: string;
    amount: number;
  };
  isChargeable: boolean;
  tax: {
    amount: number;
    rate: number;
  };
  proration: {
    amount: number;
    newPlanProratedCost: number;
    dailyPrice: number;
    remainingDays: number;
  };
  unusedBalance: number;
};

export async function getSubscriptionMigratePreview(
  params: GetSubscriptionMigratePreviewParams,
) {
  const url = "/subscription/migrate/preview";
  const response = await subscriptionAxios.post<SubscriptionMigratePreview>(
    url,
    params,
  );
  return response.data;
}

export interface CreatePlanCategoryBody {
  name: string;
}

export interface CreatePlanCategoryResult {
  planCategoryUuid: string;
}

export async function postPlanCategory(body: CreatePlanCategoryBody) {
  const url = `/plan-category`;
  const response = await subscriptionAxios.post<CreatePlanCategoryResult>(
    url,
    body,
  );
  return response.data;
}

export interface UpdatePlanCategoryBody {
  planCategoryUuid: string;
  name: string;
}

export interface UpdatePlanCategoryResult {
  planCategoryUuid: string;
}

export async function patchPlanCategory({
  planCategoryUuid,
  ...body
}: UpdatePlanCategoryBody) {
  const url = `/plan-category/${planCategoryUuid}`;
  const response = await subscriptionAxios.patch<UpdatePlanCategoryResult>(
    url,
    body,
  );
  return response.data;
}

export interface DeletePlanCategoryBody {
  planCategoryUuid: string;
}

export async function deletePlanCategory({
  planCategoryUuid,
}: DeletePlanCategoryBody) {
  const url = `/plan-category/${planCategoryUuid}`;
  const response = await subscriptionAxios.delete<void>(url);
  return response.data;
}
